import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Wifi, 
  WifiOff, 
  Database, 
  RefreshCw, 
  CheckCircle2,
  AlertCircle,
  Smartphone,
  Monitor
} from 'lucide-react';

interface OfflineInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OfflineInfoDialog({ open, onOpenChange }: OfflineInfoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WifiOff className="h-5 w-5 text-warning" />
            Como funciona o Modo Offline
          </DialogTitle>
          <DialogDescription>
            O PDV continua funcionando mesmo sem internet
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* No installation needed */}
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div>
                <h4 className="font-medium text-success">Não precisa instalar nada!</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  O modo offline usa tecnologias nativas do navegador. 
                  Funciona automaticamente em qualquer computador ou celular moderno.
                </p>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="space-y-4">
            <h4 className="font-medium">Como funciona:</h4>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Database className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Armazenamento Local</p>
                  <p className="text-xs text-muted-foreground">
                    Pedidos são salvos no IndexedDB, um banco de dados 
                    integrado ao navegador (Chrome, Firefox, Safari, Edge).
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <RefreshCw className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Sincronização Automática</p>
                  <p className="text-xs text-muted-foreground">
                    Quando a internet voltar, os pedidos são enviados 
                    automaticamente para o servidor.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Wifi className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Cache Inteligente</p>
                  <p className="text-xs text-muted-foreground">
                    Produtos e categorias ficam em cache para 
                    funcionarem mesmo offline.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What's cached */}
          <div className="space-y-3">
            <h4 className="font-medium">O que funciona offline (PDV):</h4>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Produtos</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Categorias</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Formas de Pagamento</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Mesas</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Comandas</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-success/10 rounded-lg text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>Criação de Pedidos</span>
              </div>
            </div>
          </div>

          {/* What's NOT cached */}
          <div className="space-y-3">
            <h4 className="font-medium">Apenas online (para economizar espaço):</h4>
            
            <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Relatórios</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Configurações</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Histórico de pedidos</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Estoque/CMV</span>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-3">
            <h4 className="font-medium">Requisitos:</h4>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 border rounded-lg">
                <Monitor className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Computador</p>
                  <p className="text-xs text-muted-foreground">Chrome, Firefox, Edge</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 border rounded-lg">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Celular</p>
                  <p className="text-xs text-muted-foreground">Safari, Chrome</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="space-y-3">
            <h4 className="font-medium">Dicas importantes:</h4>
            
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Acesse o PDV pelo menos uma vez com internet para carregar os produtos
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Pedidos offline são identificados com número temporário (OFF-...)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Após sincronizar, os pedidos recebem numeração oficial
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                O indicador no canto superior mostra quantos pedidos estão pendentes
              </li>
            </ul>
          </div>

          {/* Warning */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-warning mt-0.5" />
              <div>
                <h4 className="font-medium text-warning">Atenção</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Evite limpar os dados do navegador enquanto houver pedidos 
                  pendentes de sincronização, pois eles serão perdidos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
